/**
 * Portal del Empleado — PCC (acceso público sin auth, vía token).
 * Ruta: /pcc-portal/:token
 *
 * Permite al empleado: ver el comparativo, elegir aseguradora, recibir OTP,
 * firmar canvas y descargar su constancia.
 */
import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import {
  Building2, CheckCircle2, Loader2, ShieldX, Clock, ArrowRight, Eye,
  FileSignature, ShieldCheck, RefreshCw, Download, FileCheck2, Lock, Star,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PCCPortalPage() {
  const { token } = useParams();
  const [status, setStatus] = useState("loading");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null); // ahora guarda plan_id en lugar de aseguradora_id
  const [step, setStep] = useState("ver"); // ver | confirmar | firmar | listo
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState(false);
  const [otpEmail, setOtpEmail] = useState("");
  const [otpCodeDemo, setOtpCodeDemo] = useState(""); // demo only
  const [signed, setSigned] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [folio, setFolio] = useState(null);
  const canvasRef = useRef(null);
  const [drawing, setDrawing] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API_URL}/api/pcc/portal/${token}`);
      const d = await r.json();
      if (!r.ok) { setError(d.detail || "Enlace no válido"); setStatus("error"); return; }
      setData(d);
      setStatus("ok");
      if (d.firmada || d.estado === "firmada") {
        setStep("listo");
        setFolio({ folio: "—" });
      }
      if (d.seleccion) {
        // Compat: usar plan_id si existe, sino aseguradora_id (cotizaciones viejas)
        setSelected(d.seleccion.plan_id || d.seleccion.aseguradora_id);
        if (!d.firmada) setStep("confirmar");
      }
    } catch { setError("No se pudo cargar"); setStatus("error"); }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  // Canvas firma
  useEffect(() => {
    const c = canvasRef.current;
    if (!c || step !== "firmar") return;
    const ctx = c.getContext("2d");
    ctx.lineWidth = 2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#193966";
  }, [step]);

  const startDraw = (e) => {
    setDrawing(true);
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const ctx = c.getContext("2d");
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!drawing) return;
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const ctx = c.getContext("2d");
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    ctx.lineTo(x, y); ctx.stroke(); setSigned(true);
  };
  const endDraw = () => setDrawing(false);
  const clearSig = () => { const c = canvasRef.current; if (!c) return; c.getContext("2d").clearRect(0, 0, c.width, c.height); setSigned(false); };

  const handleSeleccionar = async () => {
    if (!selected) { toast.error("Elegí un plan"); return; }
    // Buscar la opción correspondiente para saber si es plan_id o aseguradora_id (compat)
    const opcion = data.opciones.find(o => o.plan_id === selected || o.aseguradora_id === selected);
    const body = opcion?.plan_id ? { plan_id: opcion.plan_id } : { aseguradora_id: selected };
    try {
      const r = await fetch(`${API_URL}/api/pcc/portal/${token}/seleccionar`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      toast.success("Selección registrada");
      setStep("confirmar");
      load();
    } catch (err) { toast.error(err.message); }
  };

  const handleEnviarOTP = async () => {
    try {
      const r = await fetch(`${API_URL}/api/pcc/portal/${token}/otp/enviar`, { method: "POST" });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      setOtpSent(true); setOtpEmail(d.email); setOtpCodeDemo(d.codigo_demo || "");
      setStep("firmar");
      toast.success("Código enviado");
    } catch (err) { toast.error(err.message); }
  };

  const handleFirmar = async () => {
    if (!signed) { toast.error("Firmá primero"); return; }
    if (otp.length !== 6) { toast.error("Ingresá el código de 6 dígitos"); return; }
    setSubmitting(true);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      const r = await fetch(`${API_URL}/api/pcc/portal/${token}/firmar`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ codigo_otp: otp, firma_imagen: dataUrl }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail);
      toast.success("¡Aprobación registrada!");
      setFolio(d);
      setStep("listo");
      load();
    } catch (err) { toast.error(err.message); }
    setSubmitting(false);
  };

  if (status === "loading") {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#193966]" /></div>;
  }
  if (status === "error") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6">
        <Card className="max-w-md text-center shadow-xl border-rose-200">
          <CardContent className="p-8">
            <ShieldX className="w-14 h-14 mx-auto text-rose-500 mb-3" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">Enlace no disponible</h2>
            <p className="text-sm text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const veh = data.vehiculo;
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/20 to-emerald-50/10" data-testid="pcc-portal-page">
      {/* Header */}
      <header className="bg-gradient-to-r from-[#193966] to-[#0F2240] text-white shadow-xl">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-xl bg-emerald-400/20 backdrop-blur-sm flex items-center justify-center border border-emerald-300/30">
              <Building2 className="w-6 h-6 text-emerald-300" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-emerald-300 font-bold">PORTAL DEL EMPLEADO · ALNAP</p>
              <h1 className="text-lg font-bold">Hola {veh.empleado_nombre} 👋</h1>
              <p className="text-xs text-blue-100/80">Cotización para {veh.marca} {veh.modelo} {veh.anio}</p>
            </div>
          </div>
          <Badge className="bg-amber-400 text-amber-950 font-bold">
            <Clock className="w-3 h-3 mr-1" />
            Vence {new Date(data.expires_at).toLocaleDateString("es-DO")}
          </Badge>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-6">
        {step === "listo" ? (
          <Card className="border-2 border-emerald-500 shadow-2xl bg-white">
            <CardContent className="p-8 text-center" data-testid="portal-success">
              <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center mb-4">
                <CheckCircle2 className="w-10 h-10 text-emerald-600" />
              </div>
              <h2 className="text-2xl font-bold text-slate-800 mb-1">¡Aprobación registrada!</h2>
              <p className="text-sm text-slate-600">Tu selección y firma quedaron registradas oficialmente.</p>
              {folio?.folio && <p className="text-xs text-slate-500 mt-2 font-mono">Folio: {folio.folio}</p>}
              <p className="text-xs text-slate-500 mt-1">El equipo de QAsesores procederá con la emisión de tu póliza.</p>
              <Button onClick={() => window.open(`${API_URL}/api/pcc/portal/${token}/constancia.pdf`, "_blank")} className="mt-6 bg-[#193966] hover:bg-[#0F2240] text-white" data-testid="portal-download-constancia">
                <Download className="w-4 h-4 mr-2" /> Descargar mi constancia (PDF)
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Paso 1: ver comparativo */}
            {step === "ver" && (
              <>
                <p className="text-sm text-slate-600">Elegí el plan con el que querés contratar tu seguro vehicular:</p>
                {data.opciones.some(o => o.recomendado) && (
                  <div className="bg-amber-50 border border-amber-300 rounded-lg p-3 flex items-center gap-2 text-sm">
                    <Star className="w-4 h-4 fill-amber-400 text-amber-500 flex-shrink-0" />
                    <span className="text-amber-900"><b>Recomendación de tu corredor:</b> revisá la opción destacada con la estrella amarilla. Vos podés elegir cualquiera de las opciones disponibles.</span>
                  </div>
                )}
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {[...data.opciones.filter(o => !o.error)].sort((a, b) => (b.recomendado ? 1 : 0) - (a.recomendado ? 1 : 0)).map(o => {
                    const optionKey = o.plan_id || o.aseguradora_id;
                    const sel = selected === optionKey;
                    const isReco = Boolean(o.recomendado);
                    return (
                      <button
                        key={optionKey}
                        onClick={() => setSelected(optionKey)}
                        data-testid={`portal-opt-${optionKey}`}
                        className={`relative text-left p-4 rounded-xl border-2 transition-all bg-white ${
                          sel ? "border-emerald-500 ring-4 ring-emerald-200"
                          : isReco ? "border-amber-400 ring-2 ring-amber-100 shadow-md"
                          : "border-slate-200 hover:border-[#193966]"
                        }`}
                      >
                        {isReco && (
                          <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-400 text-amber-900 text-[10px] font-bold px-3 py-1 rounded-full shadow-md flex items-center gap-1 whitespace-nowrap">
                            <Star className="w-3 h-3 fill-amber-900" />
                            RECOMENDADO PARA VOS
                          </div>
                        )}
                        {sel && <CheckCircle2 className="absolute top-3 right-3 w-5 h-5 text-emerald-500" />}
                        <div className="mb-2">
                          <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">{o.aseguradora_nombre}</p>
                          <h3 className="text-sm font-bold text-slate-800">{o.plan_nombre || "Plan único"}</h3>
                          {o.plan_descripcion && <p className="text-[11px] text-slate-500 mt-1 line-clamp-2">{o.plan_descripcion}</p>}
                        </div>
                        <p className="text-2xl font-bold text-[#193966]">DOP {Number(o.prima_anual).toLocaleString("es-DO")}</p>
                        <p className="text-xs text-slate-500 mb-3">Prima anual · Tasa {o.tasa_aplicada}% · Deducible DOP {Number(o.deducible).toLocaleString("es-DO")}</p>
                        <ul className="text-xs space-y-1 text-slate-600">
                          {(o.coberturas || []).slice(0, 5).map((c, i) => (
                            <li key={i} className="flex items-start gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500 mt-0.5 flex-shrink-0" /> {c.nombre}</li>
                          ))}
                          {(o.coberturas || []).length > 5 && (
                            <li className="text-slate-400 italic">+ {(o.coberturas || []).length - 5} coberturas más</li>
                          )}
                        </ul>
                        <div className={`text-center text-xs font-bold py-2 rounded mt-3 ${sel ? "bg-emerald-500 text-white" : "bg-slate-100 text-slate-600"}`}>
                          {sel ? "✓ ELEGIDO" : "Elegir este plan"}
                        </div>
                      </button>
                    );
                  })}
                </div>
                <Button disabled={!selected} onClick={handleSeleccionar} className="w-full bg-[#80BC00] hover:bg-[#6ea700] h-11 text-base font-bold" data-testid="portal-seleccionar-btn">
                  Continuar con esta selección <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </>
            )}

            {/* Paso 2: confirmar y solicitar OTP */}
            {step === "confirmar" && (
              <Card className="shadow-md max-w-2xl mx-auto">
                <CardContent className="p-6 space-y-4">
                  <div className="text-center">
                    <ShieldCheck className="w-12 h-12 text-emerald-600 mx-auto mb-2" />
                    <h2 className="text-xl font-bold">Confirmá tu selección</h2>
                  </div>
                  <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm">
                    <p className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">{data.seleccion?.aseguradora_nombre}</p>
                    <p className="font-bold text-emerald-900 text-base">{data.seleccion?.plan_nombre || "Plan elegido"}</p>
                    <p className="text-emerald-700 mt-2">Prima anual: <b>DOP {Number(data.seleccion?.prima_anual).toLocaleString("es-DO")}</b></p>
                    <p className="text-emerald-700">Deducible: <b>DOP {Number(data.seleccion?.deducible || 0).toLocaleString("es-DO")}</b></p>
                  </div>
                  <p className="text-xs text-slate-600">Para firmar te enviaremos un código de verificación de 6 dígitos a tu correo <b>{veh.email}</b>.</p>
                  <Button onClick={handleEnviarOTP} className="w-full bg-[#193966] hover:bg-[#0F2240] text-white h-11" data-testid="portal-enviar-otp-btn">
                    Enviar código y proceder a firmar
                  </Button>
                  <Button variant="outline" onClick={() => { setSelected(null); setStep("ver"); }} className="w-full" data-testid="portal-back-ver">
                    ← Cambiar de plan
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Paso 3: firma + OTP */}
            {step === "firmar" && (
              <Card className="shadow-md max-w-2xl mx-auto">
                <CardContent className="p-6 space-y-4">
                  <div className="text-center">
                    <FileSignature className="w-12 h-12 text-[#193966] mx-auto mb-2" />
                    <h2 className="text-xl font-bold">Firmar aprobación</h2>
                  </div>
                  <div>
                    <p className="text-sm font-semibold mb-2">Tu firma:</p>
                    <div className="border-2 border-dashed border-slate-300 rounded bg-slate-50">
                      <canvas
                        ref={canvasRef} width={600} height={150}
                        className="w-full h-[150px] cursor-crosshair touch-none"
                        onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
                        onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
                        data-testid="portal-canvas"
                      />
                    </div>
                    <div className="flex justify-between mt-1">
                      <span className="text-xs text-slate-400">{signed ? "✓ Firma capturada" : "Esperando firma…"}</span>
                      <button onClick={clearSig} className="text-xs text-rose-600 font-semibold hover:underline">Limpiar</button>
                    </div>
                  </div>
                  <div>
                    <p className="text-sm font-semibold mb-1">Código de verificación (OTP)</p>
                    <p className="text-xs text-slate-500 mb-2">Enviado a <b>{otpEmail}</b></p>
                    <Input
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      placeholder="• • • • • •"
                      className="text-center text-2xl tracking-[0.5em] font-bold h-12 max-w-[280px]"
                      data-testid="portal-otp-input"
                    />
                    {otpCodeDemo && (
                      <p className="text-[10px] text-amber-700 mt-1">⚠️ Demo: el código es <span className="font-mono font-bold">{otpCodeDemo}</span>. En producción esto solo se envía por email.</p>
                    )}
                  </div>
                  <Button onClick={handleFirmar} disabled={submitting || !signed || otp.length !== 6} className="w-full bg-emerald-600 hover:bg-emerald-700 h-11 text-base font-bold" data-testid="portal-firmar-btn">
                    {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    <CheckCircle2 className="w-4 h-4 mr-2" /> Firmar y aprobar
                  </Button>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </main>
      <footer className="text-center py-6 text-xs text-slate-400">
        <Lock className="w-3 h-3 inline mr-1" /> Sesión segura · Tu información está protegida<br />
        QAsesores Corredores de Seguros y Reaseguros SRL · ALNAP
      </footer>
    </div>
  );
}
